import {
  Button,
  DatePicker,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  datePickerValueToDateString,
  stringToDatePickerValue,
} from '@civilgrid/ui/components';
import { datePickerValueToYear, toLocalDate, yearToDatePickerValue } from './useBulkEditConstructionDates';

const DATE_PLACEHOLDER = 'Select Date';
const YEAR_RANGE = { start: 2020, end: 2040 };

export type EditConstructionDatesDialogProps = {
  open: boolean;
  onClose: () => void;
  constructionYear: string;
  onConstructionYearChange: (value: string) => void;
  constructionStartDate: string;
  onConstructionStartDateChange: (value: string) => void;
  constructionEndDate: string;
  onConstructionEndDateChange: (value: string) => void;
  /** True when at least one field differs from its prefill (there is something to apply). */
  canSubmit: boolean;
  onSubmit: () => void;
  isPending: boolean;
};

/** Bulk-edit modal for the Conflicts Summary table (homeViewV2's Edit Dates pattern, construction dates only). */
export function EditConstructionDatesDialog({
  open,
  onClose,
  constructionYear,
  onConstructionYearChange,
  constructionStartDate,
  onConstructionStartDateChange,
  constructionEndDate,
  onConstructionEndDateChange,
  canSubmit,
  onSubmit,
  isPending,
}: EditConstructionDatesDialogProps) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        title="Edit Construction Dates"
        preventDismiss={canSubmit}
        data-testid="conflicts-summary-edit-dates-dialog"
      >
        <DialogBody>
          <div className="grid grid-cols-3 gap-4">
            <DatePicker
              label="Construction Year"
              placeholder="Select Year"
              multiView
              views={['Year']}
              defaultView="Year"
              yearRange={YEAR_RANGE}
              allowClear={false}
              value={yearToDatePickerValue(constructionYear)}
              onChange={(v) => onConstructionYearChange(datePickerValueToYear(v))}
              className="w-full"
              data-testid="conflicts-summary-edit-dates-dialog-year"
            />
            <DatePicker
              label="Construction Start Date"
              placeholder={DATE_PLACEHOLDER}
              yearRange={YEAR_RANGE}
              allowClear={false}
              value={stringToDatePickerValue(constructionStartDate)}
              onChange={(v) => onConstructionStartDateChange(datePickerValueToDateString(v))}
              className="w-full"
              data-testid="conflicts-summary-edit-dates-dialog-start-date"
            />
            <DatePicker
              label="Construction End Date"
              placeholder={DATE_PLACEHOLDER}
              yearRange={YEAR_RANGE}
              allowClear={false}
              minDate={toLocalDate(constructionStartDate)}
              value={stringToDatePickerValue(constructionEndDate)}
              onChange={(v) => onConstructionEndDateChange(datePickerValueToDateString(v))}
              className="w-full"
              data-testid="conflicts-summary-edit-dates-dialog-end-date"
            />
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose} data-testid="conflicts-summary-edit-dates-dialog-cancel">
            Cancel
          </Button>
          <Button
            onClick={onSubmit}
            disabled={isPending || !canSubmit}
            data-testid="conflicts-summary-edit-dates-dialog-apply"
          >
            {isPending ? 'Updating…' : 'Apply'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
